import React, { useEffect, useState } from 'react'
import useCarousel from './Carousel.hook'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import HeadingSizeEnum from '../../enums/headingSize'
import pxToRem from '../../utils/pxToRem'
import IconButton from '../IconButton/IconButton'
import colors from '../../theme/colors'
import { IconTypeEnum } from '../../theme/icons'
import BulletPointBorder from '../../images/icons/bullet-point-border.svg'

import {
  SliderArrowLeft,
  SliderArrowRight,
  StyledCarourelDots,
  StyledCarouselArrows,
  StyledCarrouselControls,
  StyledCarrouselHeader,
  StyledIconButton,
  StyledSlider,
  StyledTitle,
  StyledWrapper,
} from './Carousel.styled'
import useMediaQuery from '../../hooks/useMediaQuery'
import { deviceMax } from '../../theme/breakpoints'

const ARROW = false
const AUTOPLAY = false
const INFINITE = false
const DOTS = false
const SLIDES_TO_SCROLL = 1
const SLIDES_TO_SHOW = 1
const DRAGGABLE = true

const PREV_ARROW = ({ currentSlide, slideCount, ...props }) => (
  <SliderArrowLeft {...props} />
)
const NEXT_ARROW = ({ currentSlide, slideCount, ...props }) => (
  <SliderArrowRight {...props} />
)

const BULLET_POINTS_KEY = 'bulletpoint'

export const CarouselVariantEnum = Object.freeze({
  media: 'media',
  document: 'document',
  horizontal: 'Horizontal',
  vertical: 'Vertical',
})

const Carousel = (props) => {
  const {
    children,
    className,
    initialSlide = 0,
    onChange,
    title,
    variant,
    orientation = CarouselVariantEnum.vertical,
  } = props
  const [totalSlide, setTotalSlides] = useState(1)
  const [currentSlide, setCurrentSlide] = useState(initialSlide)
  const isMobile = useMediaQuery(deviceMax.mobileL)
  const {
    listeners: { handleNextSlide, handlePrevSlide, handleGoTo },
    sliderRef,
  } = useCarousel({ children })

  useEffect(() => {
    if (variant === CarouselVariantEnum.media) setTotalSlides(children.length)
    else
      orientation === CarouselVariantEnum.vertical
        ? setTotalSlides(Math.ceil(children.length / 5))
        : setTotalSlides(Math.ceil(children.length / 3))
  }, [children])

  const handleSetCurrentSlide = (slideIndex) => {
    setCurrentSlide(slideIndex)
    onChange && onChange(slideIndex)
  }

  const sliderSettings = {
    afterChange: (slideIndex) => handleSetCurrentSlide(slideIndex),
    arrows: ARROW,
    autoplay: AUTOPLAY,
    infinite: INFINITE,
    initialSlide: initialSlide,
    dots: DOTS,
    slidesToScroll: SLIDES_TO_SCROLL,
    slidesToShow: SLIDES_TO_SHOW,
    draggable: DRAGGABLE,
    rows:
      orientation === CarouselVariantEnum.vertical &&
      variant !== CarouselVariantEnum.media
        ? 5
        : 1,
    slidesPerRow:
      orientation === CarouselVariantEnum.vertical ||
      variant === CarouselVariantEnum.media
        ? 1
        : 3,
    prevArrow: <PREV_ARROW />,
    nextArrow: <NEXT_ARROW />,
  }

  const dots =
    totalSlide > 1
      ? Array(totalSlide)
          .fill('')
          .map((dots, i) => {
            return (
              <StyledIconButton
                color={currentSlide === i ? colors.unique.black : undefined}
                height={pxToRem(10)}
                key={`${BULLET_POINTS_KEY}-${i}`}
                onClick={() => {
                  handleGoTo(i)
                }}
                type={IconTypeEnum.carrouselArrow}
                width={pxToRem(10)}
              >
                <BulletPointBorder />
              </StyledIconButton>
            )
          })
      : undefined

    return (
    <StyledWrapper
      $variant={variant}
      $orientation={!isMobile && orientation}
      className={className}
    >
      <StyledCarrouselHeader $variant={variant}>
        {title && <StyledTitle label={title} size={HeadingSizeEnum.h3} />}
        {totalSlide > 1 && (
          <StyledCarrouselControls>
            <StyledCarourelDots>
              {totalSlide && dots}
            </StyledCarourelDots>
            <StyledCarouselArrows>
              <IconButton
                color={currentSlide !== 0 ? colors.primary.blue : undefined}
                height={pxToRem(24)}
                onClick={handlePrevSlide}
                type={IconTypeEnum.carrouselArrow}
                width={pxToRem(24)}
              >
                <SliderArrowLeft />
              </IconButton>
              <IconButton
                color={
                  currentSlide !== totalSlide - 1
                    ? colors.primary.blue
                    : undefined
                }
                height={pxToRem(24)}
                onClick={handleNextSlide}
                type={IconTypeEnum.carrouselArrow}
                width={pxToRem(24)}
              >
                <SliderArrowRight />
              </IconButton>
            </StyledCarouselArrows>

          </StyledCarrouselControls>
        )}
      </StyledCarrouselHeader>
      <StyledSlider
        $isMobile={isMobile}
        $orientation={orientation}
        $variant={variant}
        ref={sliderRef}
        {...sliderSettings}
      >
        {children}
      </StyledSlider>
    </StyledWrapper>
  )
}

export default Carousel
